/**
 * SESSION PERSISTENCE UTILITY
 * 
 * This utility enables cross-tab session sharing for Firebase Authentication.
 * It allows users to open links in new tabs while maintaining their login state.
 * 
 * Key Features:
 * - Maintains authentication state across browser tabs
 * - Automatically syncs login/logout events between tabs
 * - Preserves user role and permissions across tabs
 * - Handles session cleanup when all tabs are closed
 * 
 * Implementation:
 * - Uses localStorage for cross-tab communication
 * - Uses sessionStorage for tab-specific data
 * - Listens for storage events to sync auth state
 * - Provides fallback mechanisms for edge cases
 */

import { auth } from '../config/firebase';
import { onAuthStateChanged, signOut } from 'firebase/auth';

// Storage keys for cross-tab communication
const AUTH_STATE_KEY = 'plutoo_auth_state';
const USER_DATA_KEY = 'plutoo_user_data';
const SESSION_ACTIVE_KEY = 'plutoo_session_active';
const TAB_COUNT_KEY = 'plutoo_tab_count';

// Session management
let tabId = null;
let isInitialized = false;
let authStateListeners = new Set();
let cleanupFunctions = new Set();

/**
 * Initialize session persistence system
 * This should be called once when the app starts
 */
export const initializeSessionPersistence = () => {
  if (isInitialized) {
    console.log('Session persistence already initialized');
    return;
  }

  // Generate unique tab ID
  tabId = `tab_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  
  console.log('Initializing session persistence for tab:', tabId);
  
  // Increment tab count
  const currentTabCount = parseInt(localStorage.getItem(TAB_COUNT_KEY) || '0');
  localStorage.setItem(TAB_COUNT_KEY, (currentTabCount + 1).toString());
  
  // Mark session as active
  localStorage.setItem(SESSION_ACTIVE_KEY, 'true');
  
  // Set up storage event listener for cross-tab communication
  window.addEventListener('storage', handleStorageEvent);
  
  // Set up beforeunload listener to clean up when tab closes
  window.addEventListener('beforeunload', handleTabClose);
  
  // Set up visibility change listener to handle tab focus
  document.addEventListener('visibilitychange', handleVisibilityChange);
  
  isInitialized = true;
  
  console.log('Session persistence initialized successfully');
};

/**
 * Handle storage events from other tabs
 */
const handleStorageEvent = (event) => {
  // Handle auth state changes
  if (event.key === AUTH_STATE_KEY) {
    console.log('Auth state changed in another tab:', event.newValue);
    
    // Fetch user data from localStorage when auth state is logged_in
    let userData = null;
    if (event.newValue === 'logged_in') {
      try {
        const storedUserData = localStorage.getItem(USER_DATA_KEY);
        if (storedUserData) {
          userData = JSON.parse(storedUserData);
        }
      } catch (error) {
        console.error('Error parsing user data from storage event:', error);
      }
    }
    
    // Notify all auth state listeners with correct signature: (authState, userData)
    authStateListeners.forEach(listener => {
      try {
        listener(event.newValue, userData);
      } catch (error) {
        console.error('Error in auth state listener:', error);
      }
    });
  }
  
  // Also handle user data changes (in case user data is updated separately)
  if (event.key === USER_DATA_KEY && event.newValue) {
    try {
      const authState = localStorage.getItem(AUTH_STATE_KEY);
      if (authState === 'logged_in') {
        const userData = JSON.parse(event.newValue);
        console.log('User data updated in another tab');
        
        // Notify all auth state listeners
        authStateListeners.forEach(listener => {
          try {
            listener('logged_in', userData);
          } catch (error) {
            console.error('Error in auth state listener:', error);
          }
        });
      }
    } catch (error) {
      console.error('Error handling user data storage event:', error);
    }
  }
};

/**
 * Handle tab close event
 */
const handleTabClose = () => {
  console.log('Tab closing, cleaning up session data');
  
  // Decrement tab count
  const currentTabCount = parseInt(localStorage.getItem(TAB_COUNT_KEY) || '0');
  const newTabCount = Math.max(0, currentTabCount - 1);
  
  if (newTabCount === 0) {
    // Last tab closing, clear session data
    console.log('Last tab closing, clearing session data');
    localStorage.removeItem(AUTH_STATE_KEY);
    localStorage.removeItem(USER_DATA_KEY);
    localStorage.removeItem(SESSION_ACTIVE_KEY);
    localStorage.removeItem(TAB_COUNT_KEY);
  } else {
    // Update tab count
    localStorage.setItem(TAB_COUNT_KEY, newTabCount.toString());
  }
  
  // Clean up listeners
  cleanupFunctions.forEach(cleanup => {
    try {
      cleanup();
    } catch (error) {
      console.error('Error during cleanup:', error);
    }
  });
};

/**
 * Handle visibility change (tab focus/blur)
 */
const handleVisibilityChange = () => {
  if (document.visibilityState === 'visible') {
    console.log('Tab became visible, checking auth state');
    // Check if we need to sync auth state from other tabs
    syncAuthStateFromStorage();
  }
};

/**
 * Sync authentication state from localStorage
 */
const syncAuthStateFromStorage = async () => {
  try {
    const authState = localStorage.getItem(AUTH_STATE_KEY);
    const userData = localStorage.getItem(USER_DATA_KEY);
    
    if (authState === 'logged_in' && userData) {
      const parsedUserData = JSON.parse(userData);
      console.log('Syncing user data from storage:', parsedUserData);
      
      // Notify listeners with the synced data
      authStateListeners.forEach(listener => {
        try {
          listener('logged_in', parsedUserData);
        } catch (error) {
          console.error('Error in auth sync listener:', error);
        }
      });
    }
  } catch (error) {
    console.error('Error syncing auth state from storage:', error);
  }
};

/**
 * Store authentication state in localStorage for cross-tab sharing
 * and in sessionStorage for same-tab refresh persistence.
 * sessionStorage survives a page refresh but is cleared when the tab is genuinely closed,
 * which lets checkInitialAuthState know to wait for Firebase on refresh.
 */
export const storeAuthState = (isLoggedIn, userData = null) => {
  try {
    if (isLoggedIn) {
      localStorage.setItem(AUTH_STATE_KEY, 'logged_in');
      sessionStorage.setItem(AUTH_STATE_KEY, 'logged_in');
      if (userData) {
        localStorage.setItem(USER_DATA_KEY, JSON.stringify(userData));
      }
    } else {
      localStorage.setItem(AUTH_STATE_KEY, 'logged_out');
      sessionStorage.removeItem(AUTH_STATE_KEY);
      localStorage.removeItem(USER_DATA_KEY);
    }
    
    console.log('Auth state stored:', isLoggedIn ? 'logged_in' : 'logged_out');
  } catch (error) {
    console.error('Error storing auth state:', error);
  }
};

/**
 * Get current authentication state from localStorage
 */
export const getStoredAuthState = () => {
  try {
    const authState = localStorage.getItem(AUTH_STATE_KEY);
    const userData = localStorage.getItem(USER_DATA_KEY);
    
    return {
      isLoggedIn: authState === 'logged_in',
      userData: userData ? JSON.parse(userData) : null
    };
  } catch (error) {
    console.error('Error getting stored auth state:', error);
    return { isLoggedIn: false, userData: null };
  }
};

/**
 * Get authentication state from sessionStorage.
 * sessionStorage persists through page refresh but is cleared when the tab is closed,
 * making it reliable for detecting whether a tab was refreshed vs freshly opened.
 */
export const getSessionStoredAuthState = () => {
  try {
    const authState = sessionStorage.getItem(AUTH_STATE_KEY);
    return { isLoggedIn: authState === 'logged_in' };
  } catch (error) {
    console.error('Error getting session stored auth state:', error);
    return { isLoggedIn: false };
  }
};

/**
 * Add auth state listener for cross-tab synchronization
 */
export const addAuthStateListener = (listener) => {
  authStateListeners.add(listener);
  
  // Return cleanup function
  return () => {
    authStateListeners.delete(listener);
  };
};

/**
 * Add cleanup function to be called when tab closes
 */
export const addCleanupFunction = (cleanup) => {
  cleanupFunctions.add(cleanup);
  
  // Return cleanup function
  return () => {
    cleanupFunctions.delete(cleanup);
  };
};

/**
 * Check if session is active (at least one tab is open)
 */
export const isSessionActive = () => {
  return localStorage.getItem(SESSION_ACTIVE_KEY) === 'true';
};

/**
 * Get current tab count
 */
export const getTabCount = () => {
  return parseInt(localStorage.getItem(TAB_COUNT_KEY) || '0');
};

/**
 * Clean up session persistence (call when app unmounts)
 */
export const cleanupSessionPersistence = () => {
  console.log('Cleaning up session persistence');
  
  // Remove event listeners
  window.removeEventListener('storage', handleStorageEvent);
  window.removeEventListener('beforeunload', handleTabClose);
  document.removeEventListener('visibilitychange', handleVisibilityChange);
  
  // Clear listeners
  authStateListeners.clear();
  cleanupFunctions.clear();
  
  isInitialized = false;
};

/**
 * Force logout from all tabs
 */
export const logoutAllTabs = async () => {
  try {
    console.log('Logging out from all tabs');
    
    // Clear localStorage
    localStorage.removeItem(AUTH_STATE_KEY);
    localStorage.removeItem(USER_DATA_KEY);
    localStorage.removeItem(SESSION_ACTIVE_KEY);
    localStorage.removeItem(TAB_COUNT_KEY);
    
    // Sign out from Firebase
    await signOut(auth);
    
    // Notify all tabs with correct signature
    authStateListeners.forEach(listener => {
      try {
        listener('logged_out', null);
      } catch (error) {
        console.error('Error in logout listener:', error);
      }
    });
    
    console.log('Logout from all tabs completed');
  } catch (error) {
    console.error('Error during logout from all tabs:', error);
  }
};

/**
 * Open link in new tab with session persistence
 */
export const openLinkInNewTab = (url, options = {}) => {
  try {
    // Ensure session persistence is initialized
    if (!isInitialized) {
      initializeSessionPersistence();
    }
    
    // Default options for new tab
    const defaultOptions = {
      target: '_blank',
      noopener: true,
      noreferrer: true,
      ...options
    };
    
    // Create the link element
    const link = document.createElement('a');
    link.href = url;
    link.target = defaultOptions.target;
    
    if (defaultOptions.noopener) {
      link.rel = 'noopener';
    }
    if (defaultOptions.noreferrer) {
      link.rel += ' noreferrer';
    }
    
    // Open the link
    link.click();
    
    console.log('Link opened in new tab:', url);
    return true;
  } catch (error) {
    console.error('Error opening link in new tab:', error);
    return false;
  }
};

/**
 * Open internal page in new tab with session persistence
 * This is specifically for internal application pages
 */
export const openPageInNewTab = (path, options = {}) => {
  try {
    // Ensure session persistence is initialized
    if (!isInitialized) {
      initializeSessionPersistence();
    }
    
    // Validate path
    if (!path || !path.startsWith('/')) {
      console.error('openPageInNewTab: Invalid path provided (must start with /):', path);
      return false;
    }
    
    // Construct the full URL for the internal page
    const fullUrl = `${window.location.origin}${window.location.pathname}#${path}`;
    
    // Default options for new tab
    const defaultOptions = {
      target: '_blank',
      noopener: true,
      noreferrer: true,
      ...options
    };
    
    // Create the link element
    const link = document.createElement('a');
    link.href = fullUrl;
    link.target = defaultOptions.target;
    
    if (defaultOptions.noopener) {
      link.rel = 'noopener';
    }
    if (defaultOptions.noreferrer) {
      link.rel += ' noreferrer';
    }
    
    // Open the link
    link.click();
    
    console.log('Internal page opened in new tab:', path);
    return true;
  } catch (error) {
    console.error('Error opening internal page in new tab:', error);
    return false;
  }
};

export default {
  initializeSessionPersistence,
  storeAuthState,
  getStoredAuthState,
  getSessionStoredAuthState,
  addAuthStateListener,
  addCleanupFunction,
  isSessionActive,
  getTabCount,
  cleanupSessionPersistence,
  logoutAllTabs,
  openLinkInNewTab,
  openPageInNewTab
};
